import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Facebook } from 'lucide-react';

export default function Footer() {
  return (
    <footer className="relative bg-zinc-950 text-white pt-24 pb-12 border-t border-white/5 overflow-hidden">
      {/* Background Accents */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-white/5 rounded-full blur-[120px] -translate-y-1/2" />
      <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-white/5 rounded-full blur-[100px] translate-y-1/2" />
      
      <div className="container relative z-10 mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-20">
          {/* Brand */}
          <div className="space-y-8">
            <Link to="/" className="flex flex-col group">
              <span className="text-2xl font-black tracking-normal bg-linear-to-r from-zinc-500 via-white to-zinc-500 bg-[length:200%_auto] bg-clip-text text-transparent animate-shine leading-none">
                MJ DETAILING
              </span>
              <span className="text-[10px] tracking-[0.4em] text-white/40 font-medium uppercase mt-1 group-hover:text-white/60 transition-colors">
                Premium Car Care
              </span>
            </Link>
            <p className="text-zinc-400 text-sm leading-relaxed max-w-xs">
              Eksklusiv bilpleje med fokus på perfektion. Vi leverer resultater, der taler for sig selv, med de bedste produkter på markedet.
            </p>
            <div className="flex space-x-4">
              <a href="https://www.facebook.com/profile.php?id=61582278277288" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-white hover:text-black transition-all duration-300">
                <Facebook size={18} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-xs font-bold uppercase tracking-[0.2em] mb-8 text-white/90">Hurtige Links</h4>
            <ul className="space-y-4">
              <li><Link to="/" className="text-zinc-400 hover:text-white transition-colors text-sm flex items-center gap-2 group"><span className="w-0 h-px bg-white group-hover:w-3 transition-all duration-300" />Forside</Link></li>
              <li><Link to="/om-os" className="text-zinc-400 hover:text-white transition-colors text-sm flex items-center gap-2 group"><span className="w-0 h-px bg-white group-hover:w-3 transition-all duration-300" />Om Os</Link></li>
              <li><Link to="/ydelser/rengoering" className="text-zinc-400 hover:text-white transition-colors text-sm flex items-center gap-2 group"><span className="w-0 h-px bg-white group-hover:w-3 transition-all duration-300" />Rengøring</Link></li>
              <li><Link to="/ydelser/polering" className="text-zinc-400 hover:text-white transition-colors text-sm flex items-center gap-2 group"><span className="w-0 h-px bg-white group-hover:w-3 transition-all duration-300" />Polering</Link></li>
              <li><Link to="/ydelser/coating" className="text-zinc-400 hover:text-white transition-colors text-sm flex items-center gap-2 group"><span className="w-0 h-px bg-white group-hover:w-3 transition-all duration-300" />Coating</Link></li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-xs font-bold uppercase tracking-[0.2em] mb-8 text-white/90">Kontakt</h4>
            <ul className="space-y-5">
              <li className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center shrink-0">
                  <Phone size={14} className="text-zinc-400" />
                </div>
                <a href="tel:+4527895995" className="text-zinc-400 hover:text-white transition-colors text-sm pt-1.5">+45 27 89 59 95</a>
              </li>
              <li className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center shrink-0">
                  <Mail size={14} className="text-zinc-400" />
                </div>
                <a href="mailto:mj.detailing.7190@gmail.com" className="text-zinc-400 hover:text-white transition-colors text-sm pt-1.5">mj.detailing.7190@gmail.com</a>
              </li>
              <li className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center shrink-0">
                  <MapPin size={14} className="text-zinc-400" />
                </div>
                <span className="text-zinc-400 text-sm pt-1.5">Grindstedvej 10, 7190 Billund</span>
              </li>
            </ul>
          </div>

          {/* Business Info */}
          <div>
            <h4 className="text-xs font-bold uppercase tracking-[0.2em] mb-8 text-white/90">Information</h4>
            <div className="space-y-4 text-sm text-zinc-400">
              <div className="flex justify-between border-b border-white/5 pb-2">
                <span>CVR</span>
                <span className="text-white/60">45818608</span>
              </div>
              <div className="flex justify-between border-b border-white/5 pb-2">
                <span>Man - Fre</span>
                <span className="text-white/60">08:00 - 17:00</span>
              </div>
              <div className="flex justify-between border-b border-white/5 pb-2">
                <span>Lørdag</span>
                <span className="text-white/60">Efter aftale</span>
              </div>
              <div className="flex justify-between">
                <span>Søndag</span>
                <span className="text-white/60">Lukket</span>
              </div>
            </div>
          </div>
        </div>

        <div className="pt-12 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-6">
          <p className="text-zinc-500 text-[10px] uppercase tracking-widest">
            © {new Date().getFullYear()} MJ Detailing. Crafted with precision.
          </p>
          <div className="flex gap-8">
            <Link to="/privatlivspolitik" className="text-zinc-500 hover:text-white text-[10px] uppercase tracking-widest transition-colors">Privatlivspolitik</Link>
            <Link to="/handelsbetingelser" className="text-zinc-500 hover:text-white text-[10px] uppercase tracking-widest transition-colors">Handelsbetingelser</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
